import { FC, useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Box, Tabs, Tab } from '@mui/material';

import {
  ButtonWithDoubleClickPrevention,
  Page,
  PageHeader,
  SearchBar,
} from '@/components';
import { Role } from '@/models';
import { isTargetRole, isAccountAdmin, isDispatcherRole } from '@/services';
import { useUserStore, useAgencyStore } from '@/store';

import { AgencySupportDialog } from './AgencySupportDialog';

function accessProps(index: number) {
  return {
    id: `admin-tab-${index}`,
    'aria-controls': `admin-tabpanel-${index}`,
  };
}

export const AdminLayout: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUserStore();
  const { fetchAgencies } = useAgencyStore();
  const [searchText, setSearch] = useState('');
  const [agencyModal, setAgencyModal] = useState(false);

  const isAuditLogsTab = location.pathname.includes('/admin/audit-logs');
  const isAccountConfigTab = location.pathname.includes(
    '/admin/account-configuration',
  );
  const isIncidentCodesTab = location.pathname.includes(
    '/admin/account-configuration/incident-codes',
  );
  const isLocationMarkersTab = location.pathname.includes(
    '/admin/account-configuration/location-markers',
  );

  // Check if we're in nested tabs (inside incident codes or location markers)
  const isNestedTabsView =
    location.pathname.includes(
      '/admin/account-configuration/incident-codes/',
    ) ||
    location.pathname.includes(
      '/admin/account-configuration/location-markers/',
    );

  const tabValue = isAuditLogsTab ? 1 : isAccountConfigTab ? 2 : 0;
  const subTabValue = isIncidentCodesTab ? 0 : isLocationMarkersTab ? 1 : 0;

  useEffect(() => {
    if (user) {
      fetchAgencies();
    }
  }, [user, fetchAgencies]);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: number,
  ): void => {
    if (newValue === 0) {
      navigate('/admin/agencies');
    } else if (newValue === 1) {
      navigate('/admin/audit-logs');
    } else if (newValue === 2) {
      navigate('/admin/account-configuration/incident-codes');
    }
  };

  const handleSubTabChange = (
    _event: React.SyntheticEvent,
    newValue: number,
  ): void => {
    if (newValue === 0) {
      navigate('/admin/account-configuration/incident-codes');
    } else if (newValue === 1) {
      navigate('/admin/account-configuration/location-markers');
    }
  };

  const canAddAgencies =
    user && (isAccountAdmin(user) || isDispatcherRole(user));

  const canAccessAccountConfig = user && isAccountAdmin(user);

  if (isNestedTabsView) {
    return <Outlet context={{ searchText }} />;
  }

  return (
    <Page title="Admin">
      <PageHeader title="Admin">
        {!isAuditLogsTab && !isAccountConfigTab && (
          <>
            <SearchBar
              value={searchText}
              onChange={setSearch}
              placeholder="Search..."
            />

            {canAddAgencies && (
              <ButtonWithDoubleClickPrevention
                onClick={() => setAgencyModal(true)}
                color="error"
                variant="contained"
                size="medium"
              >
                Add new agency
              </ButtonWithDoubleClickPrevention>
            )}
          </>
        )}
      </PageHeader>

      {/* Main tabs */}
      <Box sx={{ width: '100%', mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="admin tabs"
            indicatorColor="primary"
            textColor="inherit"
          >
            <Tab label="Agencies" {...accessProps(0)} />
            {user &&
              (isAccountAdmin(user) ||
                isTargetRole(user, Role.AGENCY_ADMIN)) && (
                <Tab label="Audit logs" {...accessProps(1)} />
              )}
            {canAccessAccountConfig && (
              <Tab
                label="Account Configuration"
                {...accessProps(2)}
                sx={{
                  minWidth: '140px',
                  paddingLeft: 2,
                  paddingRight: 2,
                }}
              />
            )}
          </Tabs>
        </Box>
      </Box>

      {/* Sub tabs for Account Configuration */}
      {isAccountConfigTab && (
        <Box sx={{ width: '100%', mb: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={subTabValue}
              onChange={handleSubTabChange}
              aria-label="account configuration tabs"
              indicatorColor="primary"
              textColor="inherit"
            >
              <Tab
                label="Incident Codes"
                {...accessProps(10)}
                sx={{ minWidth: '120px' }}
              />
              <Tab
                label="Location Markers"
                {...accessProps(11)}
                sx={{ minWidth: '130px' }}
              />
            </Tabs>
          </Box>
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Outlet context={{ searchText }} />
      </Box>

      <AgencySupportDialog
        open={agencyModal}
        onClose={() => setAgencyModal(false)}
      />
    </Page>
  );
};
